// src/utils/journalStore.js
//
// The built-in journal: one doc per worksheet per user at
//   users/{uid}/private_journal/{worksheetId}
// Rules make it SELF-ONLY (not org admins, not coaches, not the super admin) —
// this is free-text mental-health journaling, a stricter tier than scores.
//
// Doc shape:
//   { worksheetId, topicId, kind, title, answers: { [promptId]: value },
//     progress: { answered, total, pct }, startedAt, updatedAt, completedAt|null }
//
// Super-admin preview mode renders a synthetic user, so entries are kept
// in-session there (sessionStorage + window event), never written to Firestore.
//
// Aggregation (deliberately not wired yet): a Cloud Function with the Admin SDK
// could count completions / average scale answers per org under min-cohort
// suppression. The client never reads anyone else's journal.

import { doc, setDoc, onSnapshot, collection } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { PREVIEW_UID } from './guidedPath';

const PREVIEW_KEY = 'eri_preview_journal';
const PREVIEW_EVENT = 'eri:preview-journal';

const readPreview = () => {
    try { return JSON.parse(sessionStorage.getItem(PREVIEW_KEY) || '{}'); } catch (e) { return {}; }
};
const writePreview = (all) => {
    try { sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(all)); } catch (e) { /* noop */ }
    window.dispatchEvent(new CustomEvent(PREVIEW_EVENT, { detail: all }));
};

const deepMerge = (base, patch) => {
    const out = { ...(base || {}) };
    Object.entries(patch || {}).forEach(([k, v]) => {
        const isObj = v && typeof v === 'object' && !Array.isArray(v);
        out[k] = isObj ? deepMerge(out[k], v) : v;
    });
    return out;
};

export const journalRef = (uid, worksheetId) => doc(db, 'users', uid, 'private_journal', worksheetId);

// One entry, live. cb(entry|null)
export const subscribeJournalEntry = (uid, worksheetId, cb) => {
    if (!uid || !worksheetId) return () => {};
    if (uid === PREVIEW_UID) {
        const emit = () => cb(readPreview()[worksheetId] || null);
        emit();
        window.addEventListener(PREVIEW_EVENT, emit);
        return () => window.removeEventListener(PREVIEW_EVENT, emit);
    }
    return onSnapshot(journalRef(uid, worksheetId), (s) => cb(s.exists() ? s.data() : null),
        (e) => console.warn('journal read failed', e));
};

// Every entry for the user, live. cb(entry[])
export const subscribeJournal = (uid, cb) => {
    if (!uid) return () => {};
    if (uid === PREVIEW_UID) {
        const emit = () => cb(Object.values(readPreview()));
        emit();
        window.addEventListener(PREVIEW_EVENT, emit);
        return () => window.removeEventListener(PREVIEW_EVENT, emit);
    }
    return onSnapshot(collection(db, 'users', uid, 'private_journal'),
        (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        (e) => console.warn('journal list failed', e));
};

// Merge a patch into the entry (nested maps merge; arrays replace).
export const saveJournalEntry = async (uid, worksheetId, patch) => {
    if (!uid || !worksheetId) return;
    const stamped = { ...patch, updatedAt: new Date().toISOString() };
    if (uid === PREVIEW_UID) {
        const all = readPreview();
        all[worksheetId] = deepMerge(all[worksheetId] || { startedAt: stamped.updatedAt }, stamped);
        writePreview(all);
        return;
    }
    await setDoc(journalRef(uid, worksheetId), stamped, { merge: true });
};

// Compact, privacy-conscious summary for the AI Coach — ONLY built when the
// user has opted in (users/{uid}.journalShareWithCoach === true).
export const summarizeJournalForCoach = (entries = [], maxChars = 3000) => {
    const lines = [];
    entries
        .slice()
        .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
        .forEach(e => {
            const pct = e.progress?.pct ?? 0;
            lines.push(`- ${e.title || e.worksheetId} (${e.kind || 'worksheet'}): ${pct}% done${e.completedAt ? ', completed' : ''}, last updated ${String(e.updatedAt || '').slice(0, 10)}`);
            Object.entries(e.answers || {}).forEach(([k, v]) => {
                if (typeof v === 'number') lines.push(`  · ${k}: ${v}`);
                else if (typeof v === 'string' && v.trim()) lines.push(`  · ${k}: ${v.trim().slice(0, 140)}`);
                else if (Array.isArray(v) && v.length && typeof v[0] === 'string') lines.push(`  · ${k}: ${v.filter(Boolean).map(s => String(s).slice(0, 80)).join(' | ')}`);
            });
        });
    const text = lines.join('\n');
    return text.length > maxChars ? text.slice(0, maxChars) + '\n…' : text;
};
